import React, { useEffect, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import axios from "axios";
import { FormProvider, useForm } from "react-hook-form";
import { getErrorMessage } from "utils/errorHelpers";
import type { OrderDto, RateCard } from "client/api-v2";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { cloneOrderRateCard } from "areas/orders/services/orderService";
import { RateCardTermsFields } from "areas/rate-cards/components/RateCardTermsFields";
import type { CreateRateCardFormValues } from "areas/rate-cards/components/createRateCardForm";
import {
  collectErrorMessages,
  fractionToPercent,
  toRateCardTerms,
} from "areas/rate-cards/components/createRateCardForm";
import { getRateCardByCode } from "areas/rate-cards/services/rateCardService";

/**
 * Clones the card a quote is priced from onto negotiated terms.
 *
 * A rate card is immutable, so there is nothing to edit after a copy is taken and an exact
 * duplicate would be worth nothing. The form therefore opens seeded with the current card's prices
 * and saves whatever the representative changed as a new card, which the quote then moves onto.
 * @param root0
 * @param root0.open
 * @param root0.order
 * @param root0.onCancel
 * @param root0.onCloned
 */
export function CloneRateCardDialog({
  open,
  order,
  onCancel,
  onCloned,
}: {
  open: boolean;
  order: OrderDto;
  onCancel: () => void;
  onCloned: (order: OrderDto) => void;
}) {
  const [serverError, setServerError] = useState<string | undefined>();
  const [isLoadingSource, setIsLoadingSource] = useState(false);
  const { isAuthorized } = useRbacContext();
  const canSave = isAuthorized(RbacPolicies.RateCards.ReadWrite);

  const methods = useForm<CreateRateCardFormValues>({ defaultValues: emptyValues });
  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = methods;

  // Seeded from the card the quote holds, not from blank: the representative is negotiating away
  // from a price, so retyping the whole card to change one figure would invite a transcription
  // error into a number the customer is quoted.
  useEffect(() => {
    if (!open) {
      return;
    }

    let stale = false;
    setServerError(undefined);
    setIsLoadingSource(true);
    reset(emptyValues);

    getRateCardByCode(order.rateCardCode)
      .then((sourceCard) => {
        if (!stale) {
          reset(seededFrom(sourceCard, order.odNumber));
        }
      })
      .catch((error) => !stale && setServerError(describeSourceCardFailure(error, order.rateCardCode)))
      .finally(() => !stale && setIsLoadingSource(false));

    return () => {
      stale = true;
    };
  }, [open, order.rateCardCode, order.odNumber, reset]);

  const onSubmit = async (values: CreateRateCardFormValues) => {
    setServerError(undefined);
    try {
      onCloned(await cloneOrderRateCard(order.odNumber, toRateCardTerms(values)));
    } catch (error) {
      setServerError(getErrorMessage(error));
    }
  };

  const errorMessages = [...(serverError ? [serverError] : []), ...collectErrorMessages(errors)];

  return (
    // Escape and backdrop dismissal are ignored mid-save: the request would still complete, just
    // invisibly, leaving the quote on a card the representative was never told about.
    <Dialog open={open} onClose={() => !isSubmitting && onCancel()} maxWidth="md" fullWidth>
      <DialogTitle>Clone rate card for {order.odNumber}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* DialogContent following a DialogTitle has no top padding, which clips the first
                row's outlined-input labels without this. */}
            <Grid container spacing={3} sx={{ pt: 1 }}>
              {errorMessages.length > 0 && (
                <Grid size={12}>
                  <Alert severity="error">
                    {errorMessages.map((message) => (
                      <div key={message}>{message}</div>
                    ))}
                  </Alert>
                </Grid>
              )}

              <Grid size={12}>
                <Alert severity="info">
                  Saving creates a new rate card and moves this quote onto it. The card it is priced from now,{" "}
                  {order.rateCardCode}, is left unchanged.
                </Alert>
              </Grid>

              <RateCardTermsFields />
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={formId}
          variant="contained"
          color="primary"
          disabled={isSubmitting || isLoadingSource || !canSave}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const formId = "clone-rate-card-form";

/**
 * Why the form is empty. The card the quote is priced from could not be read, and axios says only
 * `Request failed with status code 403` — which names neither the card, nor the form it should have
 * filled in, nor the role that reads a card.
 * @param error
 * @param rateCardCode
 */
export function describeSourceCardFailure(error: unknown, rateCardCode: string): string {
  const unread = `Rate card ${rateCardCode} could not be read, so this form could not be filled in from it.`;
  return axios.isAxiosError(error) && error.response?.status === 403
    ? `${unread} Cloning a rate card needs the ${RbacPolicies.RateCards.ReadWrite} role.`
    : `${unread} ${getErrorMessage(error)}`;
}

/**
 * Numeric fields are held as strings so a representative can type freely, and are parsed on submit.
 * A card carrying no rounding sends none rather than a zero, which would round every price to the
 * nearest dollar.
 * @param sourceCard
 * @param odNumber
 */
function seededFrom(sourceCard: RateCard, odNumber: string): CreateRateCardFormValues {
  return {
    // Named for the quote, so a card in the shared list says which negotiation it came from.
    label: `${odNumber} negotiated`,
    unitPrices: sourceCard.unitPrices.map((unitPrice) => ({
      vector: unitPrice.vector,
      listPricePerUnit: String(unitPrice.listPricePerUnit),
    })),
    discountExponent: String(sourceCard.discountExponent),
    discountThresholdUnits: String(sourceCard.discountThresholdUnits),
    overallDiscountPercent: fractionToPercent(sourceCard.overallDiscountFraction),
    finalPriceRoundDownToNearest:
      sourceCard.finalPriceRoundDownToNearest === null ? "" : String(sourceCard.finalPriceRoundDownToNearest),
    fixedFees: sourceCard.fixedFees.map((fixedFee) => ({
      description: fixedFee.description,
      amount: String(fixedFee.amount),
    })),
    reason: "",
  };
}

// What the form holds while the source card is still loading. Never submitted: Save stays disabled
// until the seed lands.
const emptyValues: CreateRateCardFormValues = {
  label: "",
  unitPrices: [],
  discountExponent: "0",
  discountThresholdUnits: "0",
  overallDiscountPercent: "0",
  finalPriceRoundDownToNearest: "",
  fixedFees: [],
  reason: "",
};
